/**
 * tripStorage.ts — unified trip persistence layer.
 *
 * Signed in (userToken present): writes go to the Flask backend AND to
 * localStorage (so the UI stays reactive without waiting for the server). The
 * server copy is what the iOS/Android apps read, so it must carry the stops,
 * their kinds + nights and the trip-level planner settings — not just a name.
 *
 * Guest (no token): trips save to this device only, flagged `localOnly`. When
 * the user signs in, migrateLocalTrips() pushes them to the server
 * (POST /api/trips + POST /api/trips/<id>/stops) so they show up in the apps.
 * This mirrors the review form's progressive auth: the work survives sign-in.
 *
 * To load trips from the server on page load / after login, call
 * syncTripsFromServer() — it migrates any guest trips first, so nothing saved
 * as a guest is overwritten by the server list.
 */

import { TripStop } from '../types/trip';
import { kindFromApi, nightsFromApi, normaliseTripStops } from './tripRoles';
import {
  apiListTrips,
  apiCreateTrip,
  apiUpdateTrip,
  apiDeleteTrip,
  apiAddStop,
  apiUpdateStop,
  apiDeleteStop,
  apiReorderStops,
  isLoggedIn,
  ApiTrip,
  ApiTripOptions,
  ApiTripStop,
} from './tripApi';

export interface StoredTrip {
  id: string;
  name: string;
  createdAt: string;
  routeName?: string;
  startPoint?: string;
  endPoint?: string;
  routeKey?: string;
  stops: TripStop[];
  /** Departure date, YYYY-MM-DD. */
  startDate?: string | null;
  /** Daily driving budget in hours. */
  driveHoursPerDay?: number | null;
  /** 'car' | '4wd' | 'caravan' | 'motorhome' … */
  vehicleType?: string | null;
  /** Saved as a guest — exists on this device only until sign-in. */
  localOnly?: boolean;
}

const STORAGE_KEY = 'trip-planner.savedTrips';
const ACTIVE_TRIP_KEY = 'trip-planner.activeTrip';
// localId → serverId for every trip pushed up from this device. A planner
// draft (or any other holder) can still carry the pre-sign-in id after the
// trip has moved to the server; without this the next save didn't find it
// and created a second copy (seen live: sign in, then Share → duplicate trip).
const ID_MAP_KEY = 'trip-planner.serverIds';

const isBrowser = () => typeof window !== 'undefined';

// ─── Local helpers ──────────────────────────────────────────────────────────

export function loadSavedTrips(): StoredTrip[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredTrip[];
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

export function persistTrips(trips: StoredTrip[]): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trips));
  } catch {
    /* quota / private mode — the in-memory list still updates */
  }
}

export function saveActiveTripDraft(trip: StoredTrip | null): void {
  if (!isBrowser()) return;
  if (!trip) {
    window.localStorage.removeItem(ACTIVE_TRIP_KEY);
    return;
  }
  window.localStorage.setItem(ACTIVE_TRIP_KEY, JSON.stringify(trip));
}

export function loadActiveTripDraft(): StoredTrip | null {
  if (!isBrowser()) return null;
  const raw = window.localStorage.getItem(ACTIVE_TRIP_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredTrip;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function loadIdMap(): Record<string, string> {
  if (!isBrowser()) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ID_MAP_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function rememberServerId(localId: string, serverId: string): void {
  if (!isBrowser() || !localId || localId === serverId) return;
  try {
    const map = loadIdMap();
    map[localId] = serverId;
    window.localStorage.setItem(ID_MAP_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
}

/** The id a trip lives under now: its server id if it was pushed up from a
 *  local id, else the id itself. */
export function resolveTripId(id: string | null | undefined): string | null {
  if (!id) return null;
  return loadIdMap()[id] ?? id;
}

/** Guest trips waiting for sign-in. */
export function localOnlyTrips(trips: StoredTrip[] = loadSavedTrips()): StoredTrip[] {
  return trips.filter((t) => t.localOnly);
}

// ─── Conversion helpers ────────────────────────────────────────────────────

/** Map an API trip (Flask) to the website's StoredTrip shape. */
export function apiTripToStored(t: ApiTrip): StoredTrip {
  const stops = [...(t.stops || [])]
    .sort((a, b) => a.order_index - b.order_index)
    .map(apiStopToTripStop);
  return {
    id: t.id,
    name: t.name,
    createdAt: t.created_at,
    routeName: t.notes ?? undefined,
    startDate: t.start_date ?? null,
    driveHoursPerDay: typeof t.drive_hours_per_day === 'number' ? t.drive_hours_per_day : null,
    vehicleType: t.vehicle_type ?? null,
    stops: normaliseTripStops(stops),
  };
}

export function apiStopToTripStop(s: ApiTripStop): TripStop {
  return {
    id: s.id,
    name: s.name || '',
    kind: kindFromApi(s.kind),
    entityId: s.entity_id ?? undefined,
    lat: s.lat ?? undefined,
    lng: s.lng ?? undefined,
    nights: nightsFromApi(s.kind, s.nights),
    notes: s.notes ?? undefined,
    locked: s.locked ?? false,
    tags: s.tags,
    constraints: s.constraints,
  };
}

/** The POST /api/trips/<id>/stops body for a stop, kinds + nights per contract. */
export function stopToApiPayload(stop: TripStop, orderIndex: number) {
  return {
    entity_id: stop.entityId,
    name: stop.name,
    kind: stop.kind,
    lat: stop.lat,
    lng: stop.lng,
    nights: stop.nights ?? 0,
    notes: stop.notes,
    order_index: orderIndex,
    locked: stop.locked,
    tags: stop.tags,
    constraints: stop.constraints,
  };
}

export function tripOptions(trip: StoredTrip): ApiTripOptions {
  const opts: ApiTripOptions = { notes: trip.routeName };
  if (trip.startDate) opts.start_date = trip.startDate;
  if (typeof trip.driveHoursPerDay === 'number') opts.drive_hours_per_day = trip.driveHoursPerDay;
  if (trip.vehicleType) opts.vehicle_type = trip.vehicleType;
  return opts;
}

// ─── Server push ───────────────────────────────────────────────────────────

/**
 * Create a trip + all its stops on the server. Returns the stored trip under
 * its server id, or null if the trip itself couldn't be created. A stop that
 * fails is skipped rather than failing the whole trip.
 */
export async function pushTripToServer(trip: StoredTrip): Promise<StoredTrip | null> {
  if (!isLoggedIn()) return null;
  const created = await apiCreateTrip(trip.name, tripOptions(trip));
  if (!created) return null;
  const stops = normaliseTripStops(trip.stops);
  const savedStops: TripStop[] = [];
  for (let i = 0; i < stops.length; i++) {
    const res = await apiAddStop(created.id, stopToApiPayload(stops[i], i));
    savedStops.push(res ? { ...stops[i], id: res.id } : stops[i]);
  }
  rememberServerId(trip.id, created.id);
  return { ...trip, id: created.id, stops: savedStops, localOnly: false };
}

/** What makes two stops "the same place" when their ids differ — the
 *  planner keeps its own stop ids, so after the first server save they no
 *  longer match the server's. */
function stopIdentity(s: Pick<TripStop, 'entityId' | 'name' | 'lat' | 'lng'>): string {
  if (s.entityId != null) return `e:${s.entityId}`;
  const r = (v?: number) => (typeof v === 'number' ? v.toFixed(4) : '');
  return `p:${(s.name || '').trim().toLowerCase()}|${r(s.lat)}|${r(s.lng)}`;
}

/** The PUT body that brings a server stop in line with the local one, or
 *  null when nothing differs. */
function stopUpdates(server: TripStop, local: TripStop): Record<string, unknown> | null {
  const u: Record<string, unknown> = {};
  if (server.kind !== local.kind) u.kind = local.kind;
  if ((server.nights ?? 0) !== (local.nights ?? 0)) u.nights = local.nights ?? 0;
  if ((server.entityId ?? null) !== (local.entityId ?? null)) u.entity_id = local.entityId ?? null;
  if ((server.name || '') !== (local.name || '')) u.name = local.name;
  if (typeof local.lat === 'number' && server.lat !== local.lat) u.lat = local.lat;
  if (typeof local.lng === 'number' && server.lng !== local.lng) u.lng = local.lng;
  if (local.notes != null && (server.notes ?? '') !== local.notes) u.notes = local.notes;
  if (Object.keys(u).length === 0) return null;
  // kind and nights are validated as a pair server-side.
  if ('kind' in u || 'nights' in u) {
    u.kind = local.kind;
    u.nights = local.nights ?? 0;
  }
  return u;
}

/**
 * Bring a server trip's stops in line with the given list, changing only
 * what differs: update matched stops, delete the ones that are gone, add the
 * new ones, then fix the order. Matching is by id first, then by place.
 *
 * This used to delete every server stop and re-add the lot on any change — a
 * 17-stop trip edited three times was 34 deletes + 48 adds, a half-deleted
 * trip if the connection dropped mid-way, and fresh stop ids every save for
 * the apps (ride mode, an open trip view) to lose track of.
 */
async function replaceServerStops(tripId: string, stops: TripStop[]): Promise<TripStop[]> {
  const server = (await apiListTrips()).find((t) => t.id === tripId);
  const normalised = normaliseTripStops(stops);
  const serverStops = [...(server?.stops ?? [])]
    .sort((a, b) => a.order_index - b.order_index)
    .map(apiStopToTripStop);

  // Pair local stops with server stops: same id, else same place.
  const pairOf = new Map<number, TripStop>(); // local index → server stop
  const used = new Set<string>();
  const byId = new Map(serverStops.map((s) => [s.id, s]));
  normalised.forEach((local, i) => {
    const hit = byId.get(local.id);
    if (hit && !used.has(hit.id)) {
      pairOf.set(i, hit);
      used.add(hit.id);
    }
  });
  normalised.forEach((local, i) => {
    if (pairOf.has(i)) return;
    const key = stopIdentity(local);
    const hit = serverStops.find((s) => !used.has(s.id) && stopIdentity(s) === key);
    if (hit) {
      pairOf.set(i, hit);
      used.add(hit.id);
    }
  });

  for (const s of serverStops) {
    if (!used.has(s.id)) await apiDeleteStop(tripId, s.id);
  }

  const out: TripStop[] = [];
  const addedIds = new Set<string>();
  for (let i = 0; i < normalised.length; i++) {
    const local = normalised[i];
    const match = pairOf.get(i);
    if (match) {
      const updates = stopUpdates(match, local);
      if (updates) await apiUpdateStop(tripId, match.id, updates);
      out.push({ ...local, id: match.id });
    } else {
      const res = await apiAddStop(tripId, stopToApiPayload(local, i));
      if (res) addedIds.add(res.id);
      out.push(res ? { ...local, id: res.id } : local);
    }
  }

  // Only ids the server actually holds go into the reorder.
  const finalOrder = out.map((s) => s.id).filter((id) => used.has(id) || addedIds.has(id));
  const serverOrder = serverStops.filter((s) => used.has(s.id)).map((s) => s.id);
  const keptOrder = finalOrder.filter((id) => used.has(id));
  if (finalOrder.length > 0 && (addedIds.size > 0 || serverOrder.join('|') !== keptOrder.join('|'))) {
    await apiReorderStops(tripId, finalOrder);
  }
  return out;
}

/**
 * Push every guest (localOnly) trip to the server. Successful ones are
 * replaced in localStorage by their server copy; failures stay local and are
 * retried next time. Returns the updated list and how many moved, plus the
 * local→server id map so an open trip can follow its new id.
 */
export async function migrateLocalTrips(): Promise<{
  trips: StoredTrip[];
  migrated: number;
  idMap: Record<string, string>;
}> {
  const trips = loadSavedTrips();
  const idMap: Record<string, string> = {};
  if (!isLoggedIn()) return { trips, migrated: 0, idMap };
  let migrated = 0;
  const next: StoredTrip[] = [];
  for (const trip of trips) {
    if (!trip.localOnly) {
      next.push(trip);
      continue;
    }
    const pushed = await pushTripToServer(trip);
    if (pushed) {
      idMap[trip.id] = pushed.id;
      next.push(pushed);
      migrated += 1;
    } else {
      next.push(trip);
    }
  }
  if (migrated > 0) persistTrips(next);
  return { trips: next, migrated, idMap };
}

// ─── Server sync ───────────────────────────────────────────────────────────

/**
 * Pull trips from the server and overwrite localStorage.
 * Guest trips are migrated first so they're never lost; any that still fail
 * to upload are kept alongside the server list.
 */
export async function syncTripsFromServer(): Promise<StoredTrip[]> {
  if (!isLoggedIn()) return loadSavedTrips();

  await migrateLocalTrips();
  const stillLocal = localOnlyTrips();

  // The server doesn't store the planner's route key / endpoints. Keep them
  // from this device's copy, or the planner's same-route check can't spot
  // that a re-save is the trip it already has.
  const localById = new Map(loadSavedTrips().map((t) => [t.id, t]));
  const withLocalMeta = (t: ApiTrip): StoredTrip => {
    const stored = apiTripToStored(t);
    const local = localById.get(t.id);
    return local
      ? {
          ...stored,
          routeKey: local.routeKey,
          startPoint: local.startPoint,
          endPoint: local.endPoint,
        }
      : stored;
  };

  const serverTrips = await apiListTrips();

  if (serverTrips.length === 0) {
    // First login on an old install — trips saved before guest-save existed
    // (no localOnly flag) were never on the server. Push those up too.
    const local = loadSavedTrips().filter((t) => !t.localOnly);
    for (const trip of local) {
      await pushTripToServer(trip);
    }
    const pushed = await apiListTrips();
    const stored = [...stillLocal, ...pushed.map(withLocalMeta)];
    persistTrips(stored);
    return stored;
  }

  const stored = [...stillLocal, ...serverTrips.map(withLocalMeta)];
  persistTrips(stored);
  return stored;
}

// ─── Write-through operations ──────────────────────────────────────────────

/**
 * Save a trip (create or update).
 *  • Signed in → server first (trip + stops), then localStorage.
 *  • Guest     → localStorage only, flagged localOnly for migration on sign-in.
 * Returns the full updated list; the saved trip is first, under its final id.
 */
export async function upsertTrip(input: StoredTrip): Promise<StoredTrip[]> {
  const trips = loadSavedTrips();
  // A pre-sign-in id still held by the caller resolves to the server trip it
  // became; failing that, a server copy of the same route is the same trip.
  // Either way this is an update, not a second trip.
  const resolvedId = resolveTripId(input.id) ?? input.id;
  const byRoute =
    input.routeKey && !trips.some((t) => t.id === resolvedId)
      ? trips.find((t) => !t.localOnly && t.routeKey === input.routeKey)
      : undefined;
  const trip: StoredTrip = {
    ...input,
    id: byRoute?.id ?? resolvedId,
    createdAt: byRoute?.createdAt ?? input.createdAt,
  };
  const index = trips.findIndex((item) => item.id === trip.id);
  const withStops: StoredTrip = { ...trip, stops: normaliseTripStops(trip.stops) };

  let saved: StoredTrip;
  if (!isLoggedIn()) {
    saved = { ...withStops, localOnly: true };
  } else if (index >= 0 && !trips[index].localOnly) {
    // Existing server trip — update fields, then replace its stops.
    await apiUpdateTrip(withStops.id, withStops.name, tripOptions(withStops));
    const stops = await replaceServerStops(withStops.id, withStops.stops);
    saved = { ...withStops, stops, localOnly: false };
  } else {
    const pushed = await pushTripToServer(withStops);
    // Server down → keep it locally rather than losing it; it migrates later.
    saved = pushed ?? { ...withStops, localOnly: true };
  }

  const rest = trips.filter(
    (item) => item.id !== input.id && item.id !== trip.id && item.id !== saved.id
  );
  const next = [saved, ...rest];
  persistTrips(next);
  return next;
}

/** Delete a trip. Removes from server (if logged in) then localStorage. */
export async function deleteTrip(tripId: string): Promise<StoredTrip[]> {
  const existing = loadSavedTrips().find((t) => t.id === tripId);
  if (isLoggedIn() && !existing?.localOnly) {
    await apiDeleteTrip(tripId);
  }
  const trips = loadSavedTrips().filter((t) => t.id !== tripId);
  persistTrips(trips);
  return trips;
}

/** Add a stop to a trip. Server-first so we get the canonical stop ID. */
export async function addStopToTrip(
  tripId: string,
  stop: TripStop,
  orderIndex?: number
): Promise<StoredTrip[]> {
  const trips = loadSavedTrips();
  const idx = trips.findIndex((t) => t.id === tripId);
  if (idx < 0) return trips;

  let stopId = stop.id;

  if (isLoggedIn() && !trips[idx].localOnly) {
    const created = await apiAddStop(
      tripId,
      stopToApiPayload(stop, orderIndex ?? trips[idx].stops.length)
    );
    if (created) stopId = created.id;
  }

  trips[idx] = {
    ...trips[idx],
    stops: [...trips[idx].stops, { ...stop, id: stopId }],
  };
  persistTrips(trips);
  return trips;
}

/** Remove a stop. */
export async function removeStopFromTrip(tripId: string, stopId: string): Promise<StoredTrip[]> {
  const trips = loadSavedTrips();
  const idx = trips.findIndex((t) => t.id === tripId);
  if (isLoggedIn() && idx >= 0 && !trips[idx].localOnly) {
    await apiDeleteStop(tripId, stopId);
  }
  if (idx >= 0) {
    trips[idx] = { ...trips[idx], stops: trips[idx].stops.filter((s) => s.id !== stopId) };
    persistTrips(trips);
  }
  return trips;
}

/** Reorder stops. */
export async function reorderTripStops(tripId: string, stops: TripStop[]): Promise<StoredTrip[]> {
  const trips = loadSavedTrips();
  const idx = trips.findIndex((t) => t.id === tripId);
  if (idx >= 0) {
    trips[idx] = { ...trips[idx], stops };
    persistTrips(trips);
    if (isLoggedIn() && !trips[idx].localOnly) {
      await apiReorderStops(tripId, stops.map((s) => s.id));
    }
  }
  return trips;
}

/** Update a single stop's fields. */
export async function updateTripStop(
  tripId: string,
  stopId: string,
  updates: Partial<TripStop>
): Promise<StoredTrip[]> {
  const trips = loadSavedTrips();
  const idx = trips.findIndex((t) => t.id === tripId);
  if (idx >= 0) {
    trips[idx] = {
      ...trips[idx],
      stops: trips[idx].stops.map((s) => (s.id === stopId ? { ...s, ...updates } : s)),
    };
    persistTrips(trips);
    if (isLoggedIn() && !trips[idx].localOnly) {
      await apiUpdateStop(tripId, stopId, {
        name: updates.name,
        kind: updates.kind,
        lat: updates.lat,
        lng: updates.lng,
        nights: updates.nights,
        notes: updates.notes,
        locked: updates.locked,
        tags: updates.tags,
        constraints: updates.constraints,
        ...(updates.entityId !== undefined ? { entity_id: updates.entityId } : {}),
      });
    }
  }
  return trips;
}
